import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Aviso, Botao, Campo, CampoDeSelecao, CampoDeTexto, Modal } from '../componentes/ui';
import { listarColaboradores } from '../servicos/empresas';
import { ErroDaApi } from './ErroDaApi';

export interface DadosDoEnvio {
  readonly destinatario: string;
  readonly assunto: string;
  readonly mensagem?: string;
}

export interface ResultadoDoEnvio {
  readonly destinatarios: readonly string[];
  /** O servidor montou e não mandou (`EMAIL_ENVIO_REAL=0`). */
  readonly ensaio?: boolean;
}

/**
 * "Enviar por e-mail" de uma simulação — Passivo, Pontual.
 *
 * ## Digitado ou escolhido
 *
 * O destinatário é um campo de e-mail livre; quando a tela sabe de qual
 * empresa é a proposta (`empresaId`), os colaboradores dela aparecem num
 * seletor que preenche o campo — o caso comum é mandar ao financeiro que já
 * está cadastrado, e digitar de novo é onde o e-mail sai errado.
 *
 * ## Diz o que aconteceu
 *
 * Depois do envio a janela mostra para quem saiu — que pode ser a caixa de
 * testes, quando o ambiente desvia os e-mails — ou que foi só ensaio. "Enviado"
 * sem destinatário não diz nada a quem vai ligar para confirmar.
 *
 * O que muda entre as telas (para quem é a proposta, qual cliente) entra como
 * `children`, acima do destinatário.
 */
export function ModalDeEnvioPorEmail(props: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly assuntoPadrao: string;
  /** A empresa da proposta, para oferecer os colaboradores dela. */
  readonly empresaId?: string | null;
  readonly enviar: (dados: DadosDoEnvio) => Promise<ResultadoDoEnvio>;
  readonly aoFechar: () => void;
  readonly children?: ReactNode;
}): JSX.Element {
  const [destinatario, setDestinatario] = useState('');
  const [assunto, setAssunto] = useState(props.assuntoPadrao);
  const [mensagem, setMensagem] = useState('');

  const empresaId = props.empresaId ?? null;
  const colaboradores = useQuery({
    queryKey: ['colaboradores', empresaId],
    queryFn: () => listarColaboradores(empresaId ?? ''),
    enabled: empresaId !== null,
  });
  const comEmail = (colaboradores.data ?? []).filter((c) => c.emailAddress.trim() !== '');

  const envio = useMutation({
    mutationFn: () =>
      props.enviar({
        destinatario: destinatario.trim(),
        assunto: assunto.trim(),
        ...(mensagem.trim() === '' ? {} : { mensagem: mensagem.trim() }),
      }),
  });

  const valido = /^\S+@\S+\.\S+$/.test(destinatario.trim()) && assunto.trim() !== '';
  const resultado = envio.data;

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={props.titulo}
      {...(props.descricao === undefined ? {} : { descricao: props.descricao })}
      largura="larga"
      rodape={
        resultado === undefined ? (
          <>
            <Botao variante="secundario" onClick={props.aoFechar}>
              Cancelar
            </Botao>
            <Botao disabled={!valido} carregando={envio.isPending} onClick={() => envio.mutate()}>
              Enviar
            </Botao>
          </>
        ) : (
          <Botao onClick={props.aoFechar}>Concluir</Botao>
        )
      }
    >
      {resultado !== undefined ? (
        resultado.ensaio === true ? (
          <Aviso tom="atencao" titulo="Modo ensaio — o e-mail não saiu">
            O e-mail foi montado, mas este ambiente não envia de verdade (EMAIL_ENVIO_REAL=0).
          </Aviso>
        ) : (
          <Aviso tom="positivo" titulo="E-mail enviado">
            Enviado para {resultado.destinatarios.join(', ')}.
          </Aviso>
        )
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) envio.mutate();
          }}
        >
          {props.children}

          {comEmail.length > 0 && (
            <CampoDeSelecao
              rotulo="Escolher um colaborador"
              value=""
              onChange={(e) => {
                if (e.target.value !== '') setDestinatario(e.target.value);
              }}
              opcoes={[
                { valor: '', rotulo: 'Selecione para preencher o e-mail…' },
                ...comEmail.map((c) => ({
                  valor: c.emailAddress,
                  rotulo: `${c.fullName}${c.occupation === null ? '' : ` — ${c.occupation}`} (${c.emailAddress})`,
                })),
              ]}
            />
          )}
          <Campo
            rotulo="E-mail do destinatário"
            type="email"
            obrigatorio
            autoComplete="off"
            value={destinatario}
            onChange={(e) => setDestinatario(e.target.value)}
          />
          <Campo
            rotulo="Assunto"
            obrigatorio
            maxLength={255}
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Mensagem"
            ajuda="Opcional. Aparece no e-mail acima dos números."
            rows={3}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
          />
          {envio.isError && <ErroDaApi titulo="Não foi possível enviar" erro={envio.error} />}
          <button type="submit" className="sr-only">
            Enviar
          </button>
        </form>
      )}
    </Modal>
  );
}
